import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  SettingsPageLazy,
  FilesStoragePageLazy,
  SystemEmailTemplatesPageLazy,
  EmailNotificationSettingsPageLazy,
  GoogleSyncSettingsPageLazy,
  DatabaseBrowserPageLazy,
  ImportOrganizationsPageLazy,
  DashboardListLazy,
  AiChatPageLazy,
  AiPromptsPageLazy,
  AiUsagePageLazy,
  ClientsListLazy,
  ClientsFormLazy,
  ClientsDetailLazy,
  ClientSummaryPageLazy,
  TodosFormLazy,
  TodosDetailLazy,
  TodosCalendarViewLazy,
  AppointmentsFormLazy,
  AppointmentsDetailLazy,
  AppointmentsCalendarViewLazy,
  ActivityLogsListLazy,
  ActivityLogsDetailLazy,
  CustomFieldsListLazy,
  CustomFieldsFormLazy,
  CustomFieldsDetailLazy,
  FieldConfiguratorLazy,
  ConfigurationSettingsPageLazy,
  ParametersPageLazy,
  EmailAccountsListLazy,
  EmailAccountsFormLazy,
  EmailAccountsDetailLazy,
  EmailSignaturesListLazy,
  EmailSignaturesViewLazy,
  EmailSignaturesFormLazy,
  SearchFoldersListLazy,
  SearchFoldersDetailLazy,
  SearchFoldersFormLazy,
  EmailRulesListLazy,
  EmailRulesFormLazy,
  EmailRulesDetailLazy,
  EmailLibraryListLazy,
  EmailsListLazy,
  EmailsDetailLazy,
  EmailsComposeLazy,
  TaxAuthoritiesListLazy,
  TaxAuthoritiesFormLazy,
  TaxAuthoritiesDetailLazy,
  CountriesListLazy,
  CountriesFormLazy,
  CountriesDetailLazy,
  AppStatusListLazy,
  AppStatusFormLazy,
  AppStatusDetailLazy,
  AppDurationListLazy,
  AppDurationFormLazy,
  AppDurationDetailLazy,
  TagsListLazy,
  TagsFormLazy,
  TagsDetailLazy,
  UsersListLazy,
  UsersFormLazy,
  UsersDetailLazy,
  UserPermissionsPageLazy,
  EmailLogsPageLazy,
  SystemHealthPageLazy,
  SystemHealthStatusPageLazy,
  ProfileSettingsLazy,
  FileSendUploadPageLazy,
  FileSendDownloadPageLazy,
  LazySuspense,
} from './lazyRoutes';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LoadingProvider } from './contexts/LoadingContext';
import { SWRConfigProvider } from './contexts/SWRConfig';
import { ThemeProvider } from './contexts/ThemeContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { TopbarProvider } from './contexts/TopbarContext';
import ErrorBoundary from './components/ErrorBoundary';
import GlobalLoading from './components/GlobalLoading';
import Login from './components/Login';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import RegisterFromInvitation from './components/RegisterFromInvitation';
import MainLayout from './components/Layout/MainLayout';
import PermissionRoute from './components/PermissionRoute';
import ForbiddenPage from './components/ForbiddenPage';
import NotFoundPage from './components/NotFoundPage';
import { todosCalendarPath } from './modules/crm-todos/paths';
import { appointmentsCalendarPath } from './modules/crm-appointments/paths';

/** Renders register page when URL has token; redirects to / only when logged in and no token (so invite links always open the form). */
const RegisterRoute: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  const hasToken = searchParams.get('token');
  if (isAuthenticated && !hasToken) return <Navigate to="/" replace />;
  return <RegisterFromInvitation />;
};

const LegacySearchFoldersRedirect: React.FC = () => {
  const location = useLocation();
  const next =
    location.pathname.replace(/^\/search-folders/, '/smart-folders') +
    location.search +
    location.hash;
  return <Navigate to={next} replace />;
};

const AppRoutes: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const { t } = useTranslation('common');

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600">{t('states.loading')}</div>
      </div>
    );
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/" replace /> : <Login />}
      />
      <Route
        path="/register"
        element={<RegisterRoute />}
      />
      <Route
        path="/forgot-password"
        element={isAuthenticated ? <Navigate to="/" replace /> : <ForgotPassword />}
      />
      <Route
        path="/reset-password"
        element={isAuthenticated ? <Navigate to="/" replace /> : <ResetPassword />}
      />
      <Route
        path="/s/:id"
        element={
          <LazySuspense>
            <FileSendDownloadPageLazy />
          </LazySuspense>
        }
      />
      <Route
        path="/"
        element={
          <PermissionRoute>
            <MainLayout>
              <DashboardListLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <PermissionRoute>
            <MainLayout>
              <DashboardListLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/dashboard/full"
        element={<Navigate to="/dashboard" replace />}
      />
      <Route
        path="/ai-chat"
        element={
          <PermissionRoute>
            <MainLayout>
              <AiChatPageLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/ai-prompts"
        element={
          <PermissionRoute>
            <MainLayout>
              <AiPromptsPageLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/ai-usage"
        element={
          <PermissionRoute>
            <MainLayout>
              <AiUsagePageLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/dashboard-2"
        element={<Navigate to="/dashboard" replace />}
      />
      <Route
        path="/clients"
        element={
          <PermissionRoute>
            <MainLayout>
              <ClientsListLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/clients/new"
        element={
          <PermissionRoute>
            <MainLayout>
              <ClientsFormLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/clients/:id"
        element={
          <PermissionRoute>
            <MainLayout>
              <ClientsDetailLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/clients/:id/summary"
        element={
          <PermissionRoute>
            <MainLayout>
              <ClientSummaryPageLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/clients/:id/edit"
        element={
          <PermissionRoute>
            <MainLayout>
              <ClientsFormLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/todos"
        element={<Navigate to={todosCalendarPath()} replace />}
      />
      <Route
        path="/todos/new"
        element={
          <PermissionRoute>
            <MainLayout>
              <TodosFormLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/todos/:id"
        element={
          <PermissionRoute>
            <MainLayout>
              <TodosDetailLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/todos/:id/edit"
        element={
          <PermissionRoute>
            <MainLayout>
              <TodosFormLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      {/* Appointments — list/calendar redirect to todos monthly view; detail/form routes below */}
      <Route
        path="/appointments"
        element={
          <PermissionRoute>
            <MainLayout>
              <AppointmentsCalendarViewLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/appointments_calendar_view"
        element={<Navigate to={appointmentsCalendarPath()} replace />}
      />
      <Route
        path="/appointments/new"
        element={
          <PermissionRoute>
            <MainLayout>
              <AppointmentsFormLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/appointments/:id"
        element={
          <PermissionRoute>
            <MainLayout>
              <AppointmentsDetailLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/appointments/:id/edit"
        element={
          <PermissionRoute>
            <MainLayout>
              <AppointmentsFormLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/activity-logs"
        element={
          <PermissionRoute>
            <MainLayout>
              <ActivityLogsListLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/activity-logs/:id"
        element={
          <PermissionRoute>
            <MainLayout>
              <ActivityLogsDetailLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/custom-fields"
        element={
          <PermissionRoute>
            <MainLayout>
              <CustomFieldsListLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/custom-fields/new"
        element={
          <PermissionRoute>
            <MainLayout>
              <CustomFieldsFormLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/custom-fields/:id"
        element={
          <PermissionRoute>
            <MainLayout>
              <CustomFieldsDetailLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/custom-fields/:id/edit"
        element={
          <PermissionRoute>
            <MainLayout>
              <CustomFieldsFormLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/custom-fields/:id/configure"
        element={
          <PermissionRoute>
            <MainLayout>
              <FieldConfiguratorLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/email-accounts"
        element={
          <PermissionRoute>
            <MainLayout>
              <EmailAccountsListLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/email-accounts/new"
        element={
          <PermissionRoute>
            <MainLayout>
              <EmailAccountsFormLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/email-accounts/:id"
        element={
          <PermissionRoute>
            <MainLayout>
              <EmailAccountsDetailLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/email-accounts/:id/edit"
        element={
          <PermissionRoute>
            <MainLayout>
              <EmailAccountsFormLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/email-signatures"
        element={
          <PermissionRoute>
            <MainLayout>
              <EmailSignaturesListLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/email-signatures/new"
        element={
          <PermissionRoute>
            <MainLayout>
              <EmailSignaturesFormLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/email-signatures/:id/edit"
        element={
          <PermissionRoute>
            <MainLayout>
              <EmailSignaturesFormLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/email-signatures/:id"
        element={
          <PermissionRoute>
            <MainLayout>
              <EmailSignaturesViewLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/smart-folders"
        element={
          <PermissionRoute>
            <MainLayout>
              <SearchFoldersListLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/smart-folders/new"
        element={
          <PermissionRoute>
            <MainLayout>
              <SearchFoldersFormLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/smart-folders/:id"
        element={
          <PermissionRoute>
            <MainLayout>
              <SearchFoldersDetailLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/smart-folders/:id/edit"
        element={
          <PermissionRoute>
            <MainLayout>
              <SearchFoldersFormLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route path="/search-folders/*" element={<LegacySearchFoldersRedirect />} />
      <Route
        path="/email-rules"
        element={
          <PermissionRoute>
            <MainLayout>
              <EmailRulesListLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/email-rules/new"
        element={
          <PermissionRoute>
            <MainLayout>
              <EmailRulesFormLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/email-rules/:id/edit"
        element={
          <PermissionRoute>
            <MainLayout>
              <EmailRulesFormLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/email-rules/:id"
        element={
          <PermissionRoute>
            <MainLayout>
              <EmailRulesDetailLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/library"
        element={
          <PermissionRoute>
            <MainLayout>
              <EmailLibraryListLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/file-send"
        element={
          <PermissionRoute moduleKey="emails">
            <MainLayout>
              <LazySuspense>
                <FileSendUploadPageLazy />
              </LazySuspense>
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/emails"
        element={
          <PermissionRoute>
            <MainLayout>
              <ErrorBoundary fallback={<div className="flex flex-col items-center justify-center min-h-[320px] p-6 text-center"><p className="text-gray-600 dark:text-gray-400 mb-4">Something went wrong in the email view.</p><Link to="/emails" className="text-indigo-600 hover:underline">Back to Inbox</Link></div>}>
                <EmailsListLazy />
              </ErrorBoundary>
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route path="/emails/new" element={<Navigate to="/emails/compose" replace />} />
      <Route path="/emails/assigned" element={<Navigate to="/emails?assignedToMe=true" replace />} />
      <Route path="/emails/mentions" element={<Navigate to="/emails?mentionedMe=true" replace />} />
      <Route
        path="/emails/compose"
        element={
          <PermissionRoute>
            <MainLayout>
              <ErrorBoundary fallback={<div className="flex flex-col items-center justify-center min-h-[320px] p-6 text-center"><p className="text-gray-600 dark:text-gray-400 mb-4">Something went wrong while composing.</p><Link to="/emails" className="text-indigo-600 hover:underline">Back to Inbox</Link></div>}>
                <EmailsComposeLazy />
              </ErrorBoundary>
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/emails/:id"
        element={
          <PermissionRoute>
            <MainLayout>
              <ErrorBoundary fallback={<div className="flex flex-col items-center justify-center min-h-[320px] p-6 text-center"><p className="text-gray-600 dark:text-gray-400 mb-4">Something went wrong loading this email.</p><Link to="/emails" className="text-indigo-600 hover:underline">Back to Inbox</Link></div>}>
                <EmailsDetailLazy />
              </ErrorBoundary>
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/emails/:id/reply"
        element={
          <PermissionRoute>
            <MainLayout>
              <ErrorBoundary fallback={<div className="flex flex-col items-center justify-center min-h-[320px] p-6 text-center"><p className="text-gray-600 dark:text-gray-400 mb-4">Something went wrong while composing.</p><Link to="/emails" className="text-indigo-600 hover:underline">Back to Inbox</Link></div>}>
                <EmailsComposeLazy />
              </ErrorBoundary>
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/emails/:id/reply-all"
        element={
          <PermissionRoute>
            <MainLayout>
              <ErrorBoundary fallback={<div className="flex flex-col items-center justify-center min-h-[320px] p-6 text-center"><p className="text-gray-600 dark:text-gray-400 mb-4">Something went wrong while composing.</p><Link to="/emails" className="text-indigo-600 hover:underline">Back to Inbox</Link></div>}>
                <EmailsComposeLazy />
              </ErrorBoundary>
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/emails/:id/forward"
        element={
          <PermissionRoute>
            <MainLayout>
              <ErrorBoundary fallback={<div className="flex flex-col items-center justify-center min-h-[320px] p-6 text-center"><p className="text-gray-600 dark:text-gray-400 mb-4">Something went wrong while composing.</p><Link to="/emails" className="text-indigo-600 hover:underline">Back to Inbox</Link></div>}>
                <EmailsComposeLazy />
              </ErrorBoundary>
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/todos_monthly_view"
        element={
          <PermissionRoute>
            <MainLayout>
              <TodosCalendarViewLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route path="/todos-monthly" element={<Navigate to={todosCalendarPath()} replace />} />
      <Route
        path="/configuration-settings"
        element={
          <PermissionRoute>
            <MainLayout>
              <ConfigurationSettingsPageLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/parameters"
        element={
          <PermissionRoute>
            <MainLayout>
              <ParametersPageLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      {/* Parameter modules - Business Settings */}
      <Route path="/tax_authority" element={<PermissionRoute><MainLayout><TaxAuthoritiesListLazy /></MainLayout></PermissionRoute>} />
      <Route path="/tax_authority/new" element={<PermissionRoute><MainLayout><TaxAuthoritiesFormLazy /></MainLayout></PermissionRoute>} />
      <Route path="/tax_authority/:id" element={<PermissionRoute><MainLayout><TaxAuthoritiesDetailLazy /></MainLayout></PermissionRoute>} />
      <Route path="/tax_authority/:id/edit" element={<PermissionRoute><MainLayout><TaxAuthoritiesFormLazy /></MainLayout></PermissionRoute>} />
      <Route path="/p_country" element={<PermissionRoute><MainLayout><CountriesListLazy /></MainLayout></PermissionRoute>} />
      <Route path="/p_country/new" element={<PermissionRoute><MainLayout><CountriesFormLazy /></MainLayout></PermissionRoute>} />
      <Route path="/p_country/:id" element={<PermissionRoute><MainLayout><CountriesDetailLazy /></MainLayout></PermissionRoute>} />
      <Route path="/p_country/:id/edit" element={<PermissionRoute><MainLayout><CountriesFormLazy /></MainLayout></PermissionRoute>} />
      <Route path="/app_status" element={<PermissionRoute><MainLayout><AppStatusListLazy /></MainLayout></PermissionRoute>} />
      <Route path="/app_status/new" element={<PermissionRoute><MainLayout><AppStatusFormLazy /></MainLayout></PermissionRoute>} />
      <Route path="/app_status/:id" element={<PermissionRoute><MainLayout><AppStatusDetailLazy /></MainLayout></PermissionRoute>} />
      <Route path="/app_status/:id/edit" element={<PermissionRoute><MainLayout><AppStatusFormLazy /></MainLayout></PermissionRoute>} />
      <Route path="/duration" element={<PermissionRoute><MainLayout><AppDurationListLazy /></MainLayout></PermissionRoute>} />
      <Route path="/duration/new" element={<PermissionRoute><MainLayout><AppDurationFormLazy /></MainLayout></PermissionRoute>} />
      <Route path="/duration/:id" element={<PermissionRoute><MainLayout><AppDurationDetailLazy /></MainLayout></PermissionRoute>} />
      <Route path="/duration/:id/edit" element={<PermissionRoute><MainLayout><AppDurationFormLazy /></MainLayout></PermissionRoute>} />
      {/* Parameter modules - Other */}
      <Route path="/tags" element={<PermissionRoute><MainLayout><TagsListLazy /></MainLayout></PermissionRoute>} />
      <Route path="/tags/new" element={<PermissionRoute><MainLayout><TagsFormLazy /></MainLayout></PermissionRoute>} />
      <Route path="/tags/:id" element={<PermissionRoute><MainLayout><TagsDetailLazy /></MainLayout></PermissionRoute>} />
      <Route path="/tags/:id/edit" element={<PermissionRoute><MainLayout><TagsFormLazy /></MainLayout></PermissionRoute>} />
      {/* Users module routes */}
      <Route
        path="/admin/users"
        element={
          <PermissionRoute>
            <MainLayout>
              <UsersListLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/admin/users/new"
        element={
          <PermissionRoute>
            <MainLayout>
              <UsersFormLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/admin/users/:id"
        element={
          <PermissionRoute>
            <MainLayout>
              <UsersDetailLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/admin/users/:id/edit"
        element={
          <PermissionRoute>
            <MainLayout>
              <UsersFormLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/admin/users/:id/permissions"
        element={
          <PermissionRoute>
            <MainLayout>
              <UserPermissionsPageLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      {/* Account settings (profile + preferences merged) */}
      <Route path="/profile-settings" element={<Navigate to="/preferences" replace />} />
      <Route
        path="/preferences"
        element={
          <PermissionRoute>
            <MainLayout>
              <ProfileSettingsLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <PermissionRoute>
            <MainLayout>
              <SettingsPageLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route path="/admin/settings/deleted-files" element={<Navigate to="/admin/settings/files-storage" replace />} />
      <Route
        path="/admin/settings/files-storage"
        element={
          <PermissionRoute>
            <MainLayout>
              <FilesStoragePageLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/admin/settings/system-email-templates"
        element={
          <PermissionRoute>
            <MainLayout>
              <SystemEmailTemplatesPageLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/admin/settings/email-notifications"
        element={
          <PermissionRoute>
            <MainLayout>
              <EmailNotificationSettingsPageLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/admin/settings/google-sync"
        element={
          <PermissionRoute>
            <MainLayout>
              <GoogleSyncSettingsPageLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/admin/email-logs"
        element={
          <PermissionRoute>
            <MainLayout>
              <EmailLogsPageLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/system-health"
        element={
          <PermissionRoute>
            <MainLayout>
              <SystemHealthStatusPageLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/admin/system-health"
        element={
          <PermissionRoute>
            <MainLayout>
              <SystemHealthPageLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/tools/database-browser"
        element={
          <PermissionRoute>
            <MainLayout>
              <DatabaseBrowserPageLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route
        path="/tools/import-organizations"
        element={
          <PermissionRoute>
            <MainLayout>
              <ImportOrganizationsPageLazy />
            </MainLayout>
          </PermissionRoute>
        }
      />
      <Route path="/forbidden" element={<ForbiddenPage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

function hardReload() {
  const url = window.location.origin + window.location.pathname + window.location.search;
  window.location.replace(url);
}

const AppErrorFallback = () => (
  <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, fontFamily: 'system-ui', background: '#f9fafb', color: '#111' }}>
    <div style={{ textAlign: 'center', maxWidth: 400 }}>
      <p style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Something went wrong</p>
      <p style={{ marginBottom: 16, color: '#666' }}>Reload did not recover. Try &quot;Reload page&quot; below, or close this tab and open the app again.</p>
      <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
        <button type="button" onClick={hardReload} style={{ padding: '8px 16px', cursor: 'pointer', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 8 }}>Reload page</button>
        <button
          type="button"
          onClick={() => window.open(window.location.href, '_blank', 'noopener')}
          style={{ padding: '8px 16px', cursor: 'pointer', background: '#e5e7eb', color: '#111', border: '1px solid #d1d5db', borderRadius: 8 }}
        >
          Open in new tab
        </button>
      </div>
    </div>
  </div>
);

const App: React.FC = () => {
  return (
    <ErrorBoundary fallback={<AppErrorFallback />}>
      <ThemeProvider>
        <NotificationProvider>
          <LoadingProvider>
            <Router
              future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true,
              }}
            >
              <SWRConfigProvider>
                <AuthProvider>
                  <SettingsProvider>
                    <TopbarProvider>
                      <GlobalLoading />
                      <AppRoutes />
                    </TopbarProvider>
                  </SettingsProvider>
                </AuthProvider>
              </SWRConfigProvider>
            </Router>
          </LoadingProvider>
        </NotificationProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default App;
